import React, { useState } from 'react'
import { Grid } from '@material-ui/core'
import { useFormikContext } from 'formik'
import moment from 'moment-timezone'

import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import RadioButtonNew from 'presentation/shared/components/RadioButtonNew'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'
import DatePickerNew from 'presentation/shared/components/DatePickerNew'
import ResponsiveModal from 'presentation/shared/components/ResponsiveModal'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { useStores } from 'presentation/hooks/use-stores'
import { latexAllergyOptions } from 'common/enum/latex-allergy'
import { oncologyOptions } from 'common/enum/oncology'
import { bloodTransfusionOptions } from 'common/enum/blood-transfusion'
import { techniquesList } from 'common/enum/techniques'
import { hospitalizationList } from 'common/enum/hospitalization-type'
import { HospitalizationMode } from 'common/enum/hospitalization-mode'
import { ClinicalHistoryFormValues } from 'presentation/contexts/surgical-order-v2/types'
import { ReactComponent as ArrowIcon } from 'presentation/assets/icons/open-arrow.svg'
import * as S from './styles'

const FormClinicalHistory = () => {
  const store = useStores().surgicalOrderV2
  const formik = useFormikContext<ClinicalHistoryFormValues>()
  const [showUrgencyDateModal, setShowUrgencyDateModal] = useState(false)

  const handleUrgencyDateChange = (date: string) => {
    formik.setFieldValue('clinicalHistoryExpectedDate', date)
    if (!date) return
    const daysDiff = moment(date)
      .startOf('day')
      .diff(moment().startOf('day'), 'days')
    if (daysDiff > 5) setShowUrgencyDateModal(true)
  }

  const handleUrgencyDateModalConfirm = () => {
    formik.setFieldValue('clinicalHistoryHospitalizationType', 'elective')
    formik.setFieldValue('clinicalHistoryExpectedDate', '')
    setShowUrgencyDateModal(false)
  }

  const handleChangeHospitalizationMode = (value: string) => {
    formik.setFieldValue('clinicalHistoryHospitalizationMode', value)

    // Regra de diárias por tipo:
    // - Ambulatorial: quantidade fixa em 0 (campo desabilitado)
    // - Hospital dia: quantidade fixa em 1 (campo desabilitado)
    // - Hospitalar: médico escolhe a quantidade (mínimo 1)
    if (value === 'ambulatory') {
      formik.setFieldValue('clinicalHistoryHospitalizationDailyQuantity', 0)
    } else if (value === 'day hospital') {
      formik.setFieldValue('clinicalHistoryHospitalizationDailyQuantity', 1)
    } else if (value === 'hospital') {
      const currentQuantity =
        formik.values.clinicalHistoryHospitalizationDailyQuantity
      if (!currentQuantity || currentQuantity < 1) {
        formik.setFieldValue('clinicalHistoryHospitalizationDailyQuantity', 1)
      }
    }
  }

  const hospitalizationType = formik.values.clinicalHistoryHospitalizationType
  const isUrgency = hospitalizationType === 'urgency'
  const showExpectedDate = isUrgency || hospitalizationType === 'elective'

  const handleExpectedDateChange = (date: string) => {
    if (isUrgency) {
      handleUrgencyDateChange(date)
    } else {
      formik.setFieldValue('clinicalHistoryExpectedDate', date)
    }
  }

  const handleChangeHospitalizationType = (value: string) => {
    formik.setFieldValue('clinicalHistoryHospitalizationType', value)
    // Ao voltar para "Urgência", re-valida a data já preenchida para que o
    // modal de reenquadramento (data > 5 dias) volte a ser exibido.
    if (value === 'urgency' && formik.values.clinicalHistoryExpectedDate) {
      handleUrgencyDateChange(formik.values.clinicalHistoryExpectedDate)
    }
  }

  return (
    <>
      <S.FormWrapper onSubmit={formik.handleSubmit}>
        <S.Section>
          <HeadingNew as="h3" color="textPrimary" size="medium">
            História clínica
          </HeadingNew>

          <div style={{ marginTop: '16px' }}>
            <Grid container spacing={2}>
              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  error={
                    !!(
                      formik.touched.clinicalHistoryDescription &&
                      formik.errors.clinicalHistoryDescription
                    )
                  }
                >
                  <TextAreaNew
                    label={
                      <SupportTextNew as="span" color="neutral900">
                        Descreva a história clínica
                        <br /> do paciente
                      </SupportTextNew>
                    }
                    placeholder="Escreva sobre a história clínica do paciente"
                    name="clinicalHistoryDescription"
                    rows={3}
                    maxLength={500}
                    value={formik.values.clinicalHistoryDescription}
                    onChange={formik.handleChange}
                    required
                    requiredColor="neutral900"
                  />
                </S.InputWrapper>
                {formik.touched.clinicalHistoryDescription &&
                  formik.errors.clinicalHistoryDescription && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryDescription}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  error={
                    !!(
                      formik.touched.clinicalHistoryAspects &&
                      formik.errors.clinicalHistoryAspects
                    )
                  }
                >
                  <TextAreaNew
                    label="Descreva aspectos psicológicos, espirituais, culturais e socioeconômicos a serem considerados"
                    placeholder="Escreva sobre a história clínica do paciente"
                    name="clinicalHistoryAspects"
                    rows={3}
                    maxLength={500}
                    value={formik.values.clinicalHistoryAspects}
                    onChange={formik.handleChange}
                    required
                    requiredColor="neutral900"
                  />
                </S.InputWrapper>
                {formik.touched.clinicalHistoryAspects &&
                  formik.errors.clinicalHistoryAspects && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryAspects}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  error={
                    !!(
                      formik.touched.clinicalHistoryLatexAllergy &&
                      formik.errors.clinicalHistoryLatexAllergy
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    Paciente possui alergia a látex?*
                  </SupportTextNew>
                  {latexAllergyOptions.map((opt) => (
                    <RadioButtonNew
                      key={opt.value}
                      label={opt.label}
                      labelFor={`latex_allergy_${opt.value}`}
                      showBackground={false}
                      name="clinicalHistoryLatexAllergy"
                      checked={
                        formik.values.clinicalHistoryLatexAllergy === opt.value
                      }
                      onCheck={() =>
                        formik.setFieldValue(
                          'clinicalHistoryLatexAllergy',
                          opt.value
                        )
                      }
                    />
                  ))}
                </S.InputWrapper>
                {formik.touched.clinicalHistoryLatexAllergy &&
                  formik.errors.clinicalHistoryLatexAllergy && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryLatexAllergy}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  error={
                    !!(
                      formik.touched.clinicalHistoryOtherAllergy &&
                      formik.errors.clinicalHistoryOtherAllergy
                    )
                  }
                >
                  <TextAreaNew
                    label="Paciente possui outra alergia?"
                    placeholder="Descreva alergias do paciente"
                    name="clinicalHistoryOtherAllergy"
                    rows={3}
                    maxLength={500}
                    value={formik.values.clinicalHistoryOtherAllergy}
                    onChange={formik.handleChange}
                  />
                </S.InputWrapper>
                {formik.touched.clinicalHistoryOtherAllergy &&
                  formik.errors.clinicalHistoryOtherAllergy && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryOtherAllergy}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  error={
                    !!(
                      formik.touched.clinicalHistoryOncology &&
                      formik.errors.clinicalHistoryOncology
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    Paciente oncológico ou investigação?*
                  </SupportTextNew>
                  {oncologyOptions.map((opt) => (
                    <RadioButtonNew
                      key={opt.value}
                      label={opt.label}
                      labelFor={`oncology_${opt.value}`}
                      showBackground={false}
                      name="clinicalHistoryOncology"
                      checked={
                        formik.values.clinicalHistoryOncology ===
                        opt.booleanValue
                      }
                      onCheck={() =>
                        formik.setFieldValue(
                          'clinicalHistoryOncology',
                          opt.booleanValue
                        )
                      }
                    />
                  ))}
                </S.InputWrapper>
                {formik.touched.clinicalHistoryOncology &&
                  formik.errors.clinicalHistoryOncology && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryOncology}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  error={
                    !!(
                      formik.touched.clinicalHistoryBloodTransfusion &&
                      formik.errors.clinicalHistoryBloodTransfusion
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    Paciente tem restrição a transfusão sanguínea?*
                  </SupportTextNew>
                  {bloodTransfusionOptions.map((opt) => (
                    <RadioButtonNew
                      key={opt.value}
                      label={opt.label}
                      labelFor={`transfusion_${opt.value}`}
                      showBackground={false}
                      name="clinicalHistoryBloodTransfusion"
                      checked={
                        formik.values.clinicalHistoryBloodTransfusion ===
                        opt.booleanValue
                      }
                      onCheck={() =>
                        formik.setFieldValue(
                          'clinicalHistoryBloodTransfusion',
                          opt.booleanValue
                        )
                      }
                    />
                  ))}
                </S.InputWrapper>
                {formik.touched.clinicalHistoryBloodTransfusion &&
                  formik.errors.clinicalHistoryBloodTransfusion && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryBloodTransfusion}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>
            </Grid>
          </div>
        </S.Section>

        <S.Section>
          <HeadingNew as="h3" color="textPrimary" size="medium">
            Ficha clínica
          </HeadingNew>

          <div style={{ marginTop: '16px' }}>
            <Grid container spacing={2}>
              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  minHeight="130px"
                  error={
                    !!(
                      formik.touched.clinicalHistoryTechnique &&
                      formik.errors.clinicalHistoryTechnique
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    Técnica*
                  </SupportTextNew>

                  <S.RadioGroup>
                    {techniquesList
                      .reduce((acc, curr, i) => {
                        if (i % 2 === 0) acc.push([curr])
                        else acc[acc.length - 1].push(curr)
                        return acc
                      }, [] as Array<typeof techniquesList>)
                      .map((group, index) => (
                        <div key={index}>
                          {group.map((technique) => (
                            <RadioButtonNew
                              key={technique.value}
                              label={technique.label}
                              labelFor={technique.value}
                              showBackground={false}
                              name="clinicalHistoryTechnique"
                              checked={
                                formik.values.clinicalHistoryTechnique ===
                                technique.value
                              }
                              onCheck={() =>
                                formik.setFieldValue(
                                  'clinicalHistoryTechnique',
                                  technique.value
                                )
                              }
                            />
                          ))}
                        </div>
                      ))}
                  </S.RadioGroup>
                </S.InputWrapper>
                {formik.touched.clinicalHistoryTechnique &&
                  formik.errors.clinicalHistoryTechnique && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryTechnique}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={3}>
                <S.InputWrapper
                  minHeight="130px"
                  error={
                    !!(
                      formik.touched.clinicalHistoryCongelation &&
                      formik.errors.clinicalHistoryCongelation
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    Congelação*
                  </SupportTextNew>
                  <RadioButtonNew
                    label="Sim"
                    labelFor="congelation_yes"
                    showBackground={false}
                    name="clinicalHistoryCongelation"
                    checked={formik.values.clinicalHistoryCongelation === true}
                    onCheck={() =>
                      formik.setFieldValue('clinicalHistoryCongelation', true)
                    }
                  />
                  <RadioButtonNew
                    label="Não"
                    labelFor="congelation_no"
                    showBackground={false}
                    name="clinicalHistoryCongelation"
                    checked={formik.values.clinicalHistoryCongelation === false}
                    onCheck={() =>
                      formik.setFieldValue('clinicalHistoryCongelation', false)
                    }
                  />
                </S.InputWrapper>
                {formik.touched.clinicalHistoryCongelation &&
                  formik.errors.clinicalHistoryCongelation && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryCongelation}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={3}>
                <S.InputWrapper
                  minHeight="130px"
                  error={
                    !!(
                      formik.touched.clinicalHistoryBloodReserve &&
                      formik.errors.clinicalHistoryBloodReserve
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    Reserva de sangue*
                  </SupportTextNew>
                  <RadioButtonNew
                    label="Sim"
                    labelFor="blood_reserve_yes"
                    showBackground={false}
                    name="clinicalHistoryBloodReserve"
                    checked={formik.values.clinicalHistoryBloodReserve === true}
                    onCheck={() =>
                      formik.setFieldValue('clinicalHistoryBloodReserve', true)
                    }
                  />
                  <RadioButtonNew
                    label="Não"
                    labelFor="blood_reserve_no"
                    showBackground={false}
                    name="clinicalHistoryBloodReserve"
                    checked={
                      formik.values.clinicalHistoryBloodReserve === false
                    }
                    onCheck={() =>
                      formik.setFieldValue('clinicalHistoryBloodReserve', false)
                    }
                  />
                </S.InputWrapper>
                {formik.touched.clinicalHistoryBloodReserve &&
                  formik.errors.clinicalHistoryBloodReserve && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryBloodReserve}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  minHeight="284px"
                  error={
                    !!(
                      formik.touched.clinicalHistoryHospitalizationType &&
                      formik.errors.clinicalHistoryHospitalizationType
                    ) ||
                    !!(
                      formik.touched.clinicalHistoryExpectedDate &&
                      formik.errors.clinicalHistoryExpectedDate
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    Caráter*
                  </SupportTextNew>

                  <S.RadioGroup justifyContent="space-between">
                    <div>
                      {hospitalizationList.map((hospitalization) => (
                        <RadioButtonNew
                          key={hospitalization.value}
                          label={hospitalization.label}
                          labelFor={hospitalization.value}
                          showBackground={false}
                          name="clinicalHistoryHospitalizationType"
                          checked={
                            formik.values.clinicalHistoryHospitalizationType ===
                            hospitalization.value
                          }
                          onCheck={() =>
                            handleChangeHospitalizationType(
                              hospitalization.value
                            )
                          }
                        />
                      ))}
                    </div>
                    <div>
                      {showExpectedDate && (
                        <DatePickerNew
                          labelOverlapping
                          label={
                            isUrgency
                              ? 'Data da urgência'
                              : 'Data prevista para a cirurgia'
                          }
                          labelSize="xxsmall"
                          labelWeight="semiBold"
                          labelColor="textSecondary"
                          placeholder="dd/mm/aaaa"
                          name="clinicalHistoryExpectedDate"
                          mainValue={
                            formik.values.clinicalHistoryExpectedDate || ''
                          }
                          onMainChange={handleExpectedDateChange}
                          yearRange={{
                            start: moment().toDate().getFullYear(),
                            end: moment().add(1, 'years').toDate().getFullYear()
                          }}
                          minDate={moment().toDate()}
                          maxDate={moment().add(1, 'year').toDate()}
                          required={isUrgency}
                          requiredColor="textSecondary"
                          closeOnChange
                          height="40px"
                          error={
                            formik.touched.clinicalHistoryExpectedDate &&
                            formik.errors.clinicalHistoryExpectedDate
                          }
                        />
                      )}
                    </div>
                  </S.RadioGroup>
                  {formik.values.clinicalHistoryHospitalizationType ===
                    'urgency' && (
                    <SupportTextNew
                      color="textDisabled"
                      fontFamily="secondary"
                      size="xsmall"
                      style={{ lineHeight: 1.4 }}
                    >
                      **Declaro estar ciente de que a senha será solicitada com
                      o paciente no local. Após 7 dias se procedimento não
                      realizado o pedido será automaticamente cancelado.
                    </SupportTextNew>
                  )}
                  {formik.values.clinicalHistoryHospitalizationType ===
                    'elective' && (
                    <SupportTextNew
                      color="textDisabled"
                      fontFamily="secondary"
                      size="xsmall"
                      style={{ lineHeight: 1.4 }}
                    >
                      Esta data não garante o agendamento da cirurgia, solicite
                      o agendamento a qualquer momento na tela de Agendamentos.
                      De acordo com a Agência Nacional de Saúde (ANS), o prazo
                      de autorização é de 21 dias úteis.
                    </SupportTextNew>
                  )}
                </S.InputWrapper>
                {formik.touched.clinicalHistoryHospitalizationType &&
                  formik.errors.clinicalHistoryHospitalizationType && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryHospitalizationType}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  minHeight="284px"
                  error={
                    !!(
                      formik.touched.clinicalHistoryHospitalized &&
                      formik.errors.clinicalHistoryHospitalized
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    Paciente já está internado?
                  </SupportTextNew>
                  <S.RadioGroup justifyContent="space-between">
                    <div>
                      <RadioButtonNew
                        label="Sim"
                        labelFor="hospitalized_yes"
                        showBackground={false}
                        name="clinicalHistoryHospitalized"
                        checked={
                          formik.values.clinicalHistoryHospitalized === true
                        }
                        disabled
                        onCheck={() =>
                          formik.setFieldValue(
                            'clinicalHistoryHospitalized',
                            true
                          )
                        }
                      />
                      <RadioButtonNew
                        label="Não"
                        labelFor="hospitalized_no"
                        showBackground={false}
                        name="clinicalHistoryHospitalized"
                        checked={
                          formik.values.clinicalHistoryHospitalized === false
                        }
                        disabled
                        onCheck={() =>
                          formik.setFieldValue(
                            'clinicalHistoryHospitalized',
                            false
                          )
                        }
                      />
                    </div>
                    {!!formik.values.clinicalHistoryHospitalized && (
                      <div>
                        <DatePickerNew
                          labelOverlapping
                          label="Previsão de alta"
                          labelSize="xxsmall"
                          labelWeight="semiBold"
                          labelColor="textSecondary"
                          placeholder="dd/mm/aaaa"
                          name="clinicalHistoryBullishForecastDate"
                          mainValue={
                            formik.values.clinicalHistoryBullishForecastDate ||
                            ''
                          }
                          onMainChange={(date) => {
                            formik.setFieldValue(
                              'clinicalHistoryBullishForecastDate',
                              date
                            )
                          }}
                          yearRange={{
                            start: moment().toDate().getFullYear(),
                            end: moment().add(2, 'years').toDate().getFullYear()
                          }}
                          minDate={moment().toDate()}
                          maxDate={moment().add(2, 'year').toDate()}
                          requiredColor="textSecondary"
                          closeOnChange
                          height="40px"
                          error={
                            formik.touched.clinicalHistoryBullishForecastDate &&
                            formik.errors.clinicalHistoryBullishForecastDate
                          }
                        />
                      </div>
                    )}
                  </S.RadioGroup>
                  {!!formik.values.clinicalHistoryHospitalized && (
                    <S.CardHospitalizedInfo style={{ marginTop: '16px' }}>
                      <div>
                        <SupportTextNew
                          color="info950"
                          size="xxsmall"
                          weight="semiBold"
                        >
                          Nº atendimento:
                        </SupportTextNew>
                        <SupportTextNew color="textPrimary">
                          {store.patient.internation?.cd_atendimento
                            ? store.patient.internation?.cd_atendimento
                            : '-'}
                        </SupportTextNew>
                      </div>
                      <div>
                        <SupportTextNew
                          color="info950"
                          size="xxsmall"
                          weight="semiBold"
                        >
                          Local Internação:
                        </SupportTextNew>
                        <SupportTextNew color="textPrimary">
                          {store.patient.internation?.ds_leito
                            ? store.patient.internation?.ds_leito
                            : '-'}
                        </SupportTextNew>
                      </div>
                    </S.CardHospitalizedInfo>
                  )}
                </S.InputWrapper>
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  error={
                    !!(
                      formik.touched
                        .clinicalHistoryHospitalizationDailyQuantity &&
                      formik.errors.clinicalHistoryHospitalizationDailyQuantity
                    ) ||
                    !!(
                      formik.touched.clinicalHistoryHospitalizationMode &&
                      formik.errors.clinicalHistoryHospitalizationMode
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    Diárias*
                  </SupportTextNew>
                  <S.InputGroup marginTop="58px">
                    <div>
                      <SelectFieldNew
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        requiredColor="textSecondary"
                        label="Tipo de diária"
                        name="clinicalHistoryHospitalizationMode"
                        placeholder="Selecione"
                        value={
                          formik.values.clinicalHistoryHospitalizationMode || ''
                        }
                        onInputChange={handleChangeHospitalizationMode}
                        items={[
                          {
                            label: HospitalizationMode.ambulatory,
                            value: 'ambulatory'
                          },
                          {
                            label: HospitalizationMode['day hospital'],
                            value: 'day hospital'
                          },
                          {
                            label: HospitalizationMode.hospital,
                            value: 'hospital'
                          }
                        ]}
                        height="42px"
                      />
                    </div>

                    <div>
                      <TextFieldNew
                        placeholder="Quant."
                        label="Quant."
                        labelOverlapping
                        labelSize="xxsmall"
                        labelWeight="semiBold"
                        labelColor="textSecondary"
                        requiredColor="textSecondary"
                        name="clinicalHistoryHospitalizationDailyQuantity"
                        required
                        height="40px"
                        value={
                          formik.values
                            .clinicalHistoryHospitalizationDailyQuantity
                        }
                        disabled={
                          formik.values.clinicalHistoryHospitalizationMode !==
                          'hospital'
                        }
                        onInputChange={(quantity) => {
                          const isValidQuantityInput = (value: string) => {
                            const digitRegex = /\d+/g
                            const lastChar = value.charAt(value.length - 1)

                            return (
                              (digitRegex.test(lastChar) && lastChar !== ',') ||
                              value === ''
                            )
                          }

                          if (isValidQuantityInput(quantity)) {
                            formik.setFieldValue(
                              'clinicalHistoryHospitalizationDailyQuantity',
                              Number(quantity)
                            )
                          }
                        }}
                        iconIsButton
                        keepStyleOnIconIsButton
                        icon={
                          <S.ArrowIconsWrapper>
                            <ArrowIcon
                              onClick={(e) => {
                                e.stopPropagation()
                                if (
                                  formik.values
                                    .clinicalHistoryHospitalizationMode ===
                                  'hospital'
                                ) {
                                  formik.setFieldValue(
                                    'clinicalHistoryHospitalizationDailyQuantity',
                                    formik.values
                                      .clinicalHistoryHospitalizationDailyQuantity +
                                      1
                                  )
                                }
                              }}
                            />
                            <ArrowIcon
                              onClick={(e) => {
                                e.stopPropagation()
                                if (
                                  formik.values
                                    .clinicalHistoryHospitalizationMode ===
                                  'hospital'
                                ) {
                                  const currentQty =
                                    formik.values
                                      .clinicalHistoryHospitalizationDailyQuantity
                                  formik.setFieldValue(
                                    'clinicalHistoryHospitalizationDailyQuantity',
                                    currentQty > 1 ? currentQty - 1 : currentQty
                                  )
                                }
                              }}
                            />
                          </S.ArrowIconsWrapper>
                        }
                      />
                    </div>
                  </S.InputGroup>
                </S.InputWrapper>
                {formik.touched.clinicalHistoryHospitalizationMode &&
                  formik.errors.clinicalHistoryHospitalizationMode && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryHospitalizationMode}
                    </SupportTextNew>
                  )}
                {formik.touched.clinicalHistoryHospitalizationDailyQuantity &&
                  formik.errors.clinicalHistoryHospitalizationDailyQuantity && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {
                        formik.errors
                          .clinicalHistoryHospitalizationDailyQuantity
                      }
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12} md={6}>
                <S.InputWrapper
                  error={
                    !!(
                      formik.touched.clinicalHistoryCti &&
                      formik.errors.clinicalHistoryCti
                    )
                  }
                >
                  <SupportTextNew
                    color="neutral900"
                    style={{ marginBottom: '8px' }}
                  >
                    A cirurgia vai precisar de CTI?
                  </SupportTextNew>
                  <RadioButtonNew
                    label="Sim"
                    labelFor="cti_yes"
                    showBackground={false}
                    name="clinicalHistoryUti"
                    checked={formik.values.clinicalHistoryCti === true}
                    onCheck={() =>
                      formik.setFieldValue('clinicalHistoryCti', true)
                    }
                  />
                  <RadioButtonNew
                    label="Não"
                    labelFor="cti_no"
                    showBackground={false}
                    name="clinicalHistoryCti"
                    checked={formik.values.clinicalHistoryCti === false}
                    onCheck={() =>
                      formik.setFieldValue('clinicalHistoryCti', false)
                    }
                  />
                </S.InputWrapper>
                {formik.touched.clinicalHistoryCti &&
                  formik.errors.clinicalHistoryCti && (
                    <SupportTextNew
                      color="danger300"
                      size="xsmall"
                      style={{ marginTop: '4px' }}
                    >
                      {formik.errors.clinicalHistoryCti}
                    </SupportTextNew>
                  )}
              </S.StyledGridItem>

              <S.StyledGridItem item xs={12}>
                <TextAreaNew
                  label="Observações médicas"
                  placeholder="Escreva as observações que considera importantes para o processo de autorização da cirurgia"
                  name="clinicalHistoryMedicalObservations"
                  rows={4}
                  maxLength={500}
                  value={formik.values.clinicalHistoryMedicalObservations}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.clinicalHistoryMedicalObservations &&
                    (formik.errors.clinicalHistoryMedicalObservations as
                      | string
                      | undefined)
                  }
                  required
                  requiredColor="neutral900"
                />
              </S.StyledGridItem>
            </Grid>
          </div>
        </S.Section>
      </S.FormWrapper>
      <ResponsiveModal
        isOpen={showUrgencyDateModal}
        onClose={handleUrgencyDateModalConfirm}
        onConfirm={handleUrgencyDateModalConfirm}
        title="Pedido enquadrado como eletivo"
        confirmButtonLabel="Entendi"
        hideCancelButton
        width={480}
      >
        {() => (
          <>
            <SupportTextNew color="textSecondary">
              Pedido com data superior a 5 dias será enquadrado como eletivo e
              seguirá processo de autorização da operadora com prazo de até 21
              dias.
            </SupportTextNew>
            <SupportTextNew color="textSecondary" style={{ marginTop: '24px' }}>
              A data da cirurgia deverá ser definida posteriormente no fluxo de
              Agendamento Cirúrgico.
            </SupportTextNew>
          </>
        )}
      </ResponsiveModal>
    </>
  )
}

export default FormClinicalHistory
